import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, Shield, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config/api";

const PlatformAdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // 2FA State
  const [step, setStep] = useState(1); // 1 = credentials, 2 = 2FA verification
  const [twoFACode, setTwoFACode] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [use2FA, setUse2FA] = useState(false); // Whether 2FA is required

  const handleSendCredentials = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Please enter both username and password");
      return;
    }

    setLoading(true);
    try {
      // Try PostgreSQL v2 endpoint first (no 2FA)
      const response = await axios.post(`${API_URL}/v2/super-admin/auth/login`, {
        username: username,
        password: password
      });
      
      if (response.data.success) {
        // Check if 2FA is required
        if (response.data.requires_2fa) {
          setUse2FA(true);
          setMaskedEmail(response.data.email || "your registered email");
          setStep(2);
          toast.success("Verification code sent to your email");
        } else {
          // Direct login success (no 2FA)
          localStorage.setItem("jwtToken", response.data.token);
          localStorage.setItem("userType", "super_admin");
          localStorage.setItem("userData", JSON.stringify({
            username: response.data.username,
            name: response.data.name || "Super Admin"
          }));
          
          toast.success("Welcome, Super Admin!");
          navigate("/super-admin");
        }
      }
    } catch (error) {
      console.error("Super admin login error:", error);
      
      // Fallback to old MongoDB endpoint if v2 fails
      if (error.response?.status === 404 || error.response?.status === 401) {
        try {
          const mongoResponse = await axios.post(`${API_URL}/auth/super-admin-send-2fa`, {
            username: username,
            password: password
          });
          
          if (mongoResponse.data.success) {
            setUse2FA(true);
            setMaskedEmail(mongoResponse.data.email || "your registered email");
            setStep(2);
            toast.success("Verification code sent to your email");
            return;
          }
        } catch (mongoError) {
          console.error("MongoDB fallback error:", mongoError);
        }
      }
      
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }

    setLoading(true);
    try {
      // Use MongoDB endpoint for 2FA verification (v2 doesn't support it yet)
      const response = await axios.post(`${API_URL}/auth/super-admin-verify-2fa`, {
        username: username,
        code: twoFACode
      });
      
      if (response.data.success && response.data.user_type === "super_admin") {
        localStorage.setItem("jwtToken", response.data.token);
        localStorage.setItem("userType", "super_admin");
        localStorage.setItem("userData", JSON.stringify({
          username: response.data.username,
          name: response.data.name,
          session_id: response.data.session_id
        }));
        
        toast.success("Welcome, Super Admin!");
        navigate("/super-admin");
      }
    } catch (error) {
      console.error("2FA verification error:", error);
      const msg = error.response?.data?.detail || "Invalid verification code";
      toast.error(msg);
      
      if (msg.includes("expired") || msg.includes("request a new")) {
        setStep(1);
        setTwoFACode("");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/super-admin-send-2fa`, {
        username: username,
        password: password
      });
      
      if (response.data.success) {
        toast.success("New code sent to your email");
        setTwoFACode("");
      }
    } catch (error) {
      toast.error("Failed to resend code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setTwoFACode("");
  };

  // 2FA Screen - Clean background with 6 individual boxes
  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="w-full max-w-sm text-center">
          {/* Lock Icon */}
          <div className="mb-6">
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto border-2 border-blue-400/50">
              <Lock className="w-10 h-10 text-blue-400" />
            </div>
          </div>
          
          {/* Title */}
          <h1 className="text-2xl font-bold text-white mb-2">Two-Factor Authentication</h1>
          <p className="text-blue-200 text-sm mb-8">
            Enter the 6-digit code sent to<br/>
            <span className="text-white font-medium">{maskedEmail}</span>
          </p>
          
          {/* 6 Individual Input Boxes */}
          <form onSubmit={handleVerify2FA} className="space-y-6">
            <div className="flex justify-center gap-2">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={twoFACode[index] || ''}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 1) {
                      const newCode = twoFACode.split('');
                      newCode[index] = val;
                      setTwoFACode(newCode.join(''));
                      // Auto-focus next input
                      if (val && index < 5) {
                        const nextInput = document.getElementById(`pin-${index + 1}`);
                        nextInput?.focus();
                      }
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
                      const prevInput = document.getElementById(`pin-${index - 1}`);
                      prevInput?.focus();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                    setTwoFACode(paste);
                  }}
                  id={`pin-${index}`}
                  disabled={loading}
                  className="w-12 h-14 text-center text-2xl font-bold bg-white/10 border-2 border-blue-400/50 rounded-lg text-white focus:border-blue-400 focus:bg-white/20 focus:outline-none transition-all"
                  autoFocus={index === 0}
                />
              ))}
            </div>
            
            {/* Verify Button */}
            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold bg-blue-500 hover:bg-blue-600 text-white"
              disabled={loading || twoFACode.length !== 6}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                </span>
              ) : (
                "Verify & Sign In"
              )}
            </Button>
          </form>
          
          {/* Footer Links */}
          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={handleBack}
              className="text-blue-300 hover:text-white transition-colors"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={loading}
              className="text-blue-300 hover:text-white font-medium transition-colors"
            >
              Resend Code
            </button>
          </div>
          
          <p className="text-xs text-blue-300/70 mt-4">
            Code expires in 10 minutes
          </p>
        </div>
      </div>
    );
  }

  // Step 1: Credentials screen
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: '#007AFF'}}>
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            Booking<span style={{color: '#007AFF'}}>Flows</span>
          </CardTitle>
          <p className="text-gray-600 mt-2">Platform Administrator Access</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendCredentials} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-semibold text-gray-700">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter admin username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                className="h-12 text-base border-2 border-gray-200 focus:border-blue-500"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="h-12 text-base border-2 border-gray-200 focus:border-blue-500"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold mt-6"
              style={{background: '#007AFF'}}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                </span>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Continue
                </>
              )}
            </Button>
          </form>
        </CardContent>
        
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default PlatformAdminLogin;
